import { z } from 'zod';
/**
 * The two mandatory taxonomies.
 *
 * Every content object in ONOV carries exactly one value from each. This is the
 * single source of truth for both — the API's Mongoose schemas, the mobile
 * composers and the admin console all derive from here.
 *
 * See docs/01-PRD.md §6.4, docs/03-Feature-Specification.md §0.7 and
 * AC-GL-17: creation is blocked if either value is missing. Defining them once
 * is what makes that a compile error rather than a runtime check
 * (docs/06-Technology-Stack.md §4, ADR-10).
 */
/** Category 1 — what the content is about. */
export const Category1 = z.enum(['political', 'entertainment', 'hobbies_sports', 'business']);
/** Category 2 — how far the content reaches. */
export const Category2 = z.enum(['local', 'state', 'national', 'world']);
/**
 * Mixin applied to every content object. Both fields are required with no
 * default — a default would quietly satisfy AC-GL-17 without the author ever
 * having chosen, which is exactly the failure mode docs/02 §11 warns about.
 */
export const Taxonomy = z.object({
    category1: Category1,
    category2: Category2,
});
/**
 * Seed display labels. The registry is the runtime source (FR-ADM-60) — these
 * are what a fresh database starts with and what a client without the registry
 * falls back to.
 *
 * Labels track the product design; the KEYS never move. `entertainment` reads
 * as "Entertainment/Sports" and `hobbies_sports` as "Family/Hobbies" because
 * that is how the screens group them — renaming the keys to match would mean
 * rewriting the taxonomy stamped on every post, event, business and community.
 */
export const CATEGORY1_LABELS = {
    political: 'Political',
    entertainment: 'Entertainment/Sports',
    hobbies_sports: 'Family/Hobbies',
    business: 'Business',
};
export const CATEGORY2_LABELS = {
    local: 'Local',
    state: 'State',
    national: 'National',
    world: 'World',
};
export const CATEGORY1_VALUES = Category1.options;
export const CATEGORY2_VALUES = Category2.options;
// ─────────────────────────────────────────────────────────────────────────────
// Runtime registry (FR-ADM-60, FR-ADM-62)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * A Category-1 key as it travels at RUNTIME.
 *
 * `Category1` above stays a code enum because it is stamped on every post,
 * event, business and community document — a key set that is denormalised into
 * millions of rows cannot be edited from a console (US-ADM-50: "changing these
 * changes every filter in the product"). What FR-ADM-60 does make editable is
 * the registry around it: labels, display order, enablement, and any key an
 * admin appends later.
 *
 * So: every `Category1` value is a `CategoryKey`, and the registry may hold
 * more. Profile-side surfaces (areas of interest, profile sections) accept the
 * wider type and validate against the registry; content surfaces keep the
 * narrow enum and its compile-time guarantee.
 */
export const CategoryKey = z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'lower_snake_case');
/** How one profile-section field is rendered and validated. */
/**
 * `image` holds ONE url — the storage module mints it and the app renders a
 * picker rather than a text box. It is a scalar like `text`, not a list like
 * `chips`: a section has one cover photo, not a gallery.
 */
export const ProfileFieldType = z.enum([
    'text',
    'text-list',
    'textarea',
    'select',
    'chips',
    'image',
]);
/**
 * One field on an "Account Settings <Category>" screen.
 *
 * The four screens in the design are the same screen with different field
 * lists, so the list is data rather than four hand-written forms: the app
 * renders whatever the registry describes, and the API compiles the very same
 * definitions into the zod schema it validates saves against. A fifth category
 * therefore needs no app release and no API deploy.
 */
export const ProfileSectionField = z.object({
    key: z
        .string()
        .min(1)
        .max(40)
        .regex(/^[a-zA-Z][a-zA-Z0-9]*$/, 'camelCase'),
    label: z.string().min(1).max(80),
    type: ProfileFieldType,
    required: z.boolean().default(false),
    /** `text`/`textarea`, and each entry of a `text-list`. */
    maxLength: z.number().int().positive().max(5_000).optional(),
    /** `text-list`/`chips`: how many entries the `+` affordance may add. */
    maxItems: z.number().int().positive().max(50).optional(),
    /** `select`/`chips`: the allowed values. */
    options: z.array(z.string().min(1).max(80)).max(200).optional(),
    placeholder: z.string().max(120).optional(),
});
export const ProfileSectionDefinition = z.object({
    /** Screen heading — "Create Political account". */
    title: z.string().min(1).max(120),
    fields: z.array(ProfileSectionField).max(30),
});
/**
 * One Category-1 entry as the apps read it. `key` is append-only: an admin may
 * relabel, reorder or disable, never rename or delete — a vanished key orphans
 * data in every collection that stamped it.
 */
export const CategoryDefinitionView = z.object({
    key: CategoryKey,
    label: z.string(),
    order: z.number().int(),
    enabled: z.boolean(),
    /** Absent when the category has no "area of interest" screen. */
    profileSection: ProfileSectionDefinition.optional(),
});
/** One "What I Like" chip from the catalogue backing FR-PROF-05 (FR-ADM-62). */
export const InterestOptionView = z.object({
    id: z.string(),
    category1: CategoryKey,
    label: z.string(),
    icon: z.string().optional(),
    order: z.number().int(),
});
export const InterestQuery = z.object({ category1: CategoryKey.optional() });
//# sourceMappingURL=taxonomy.js.map